import { useState, useEffect } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { AgentAvatar } from "@/workshop/components/canvas/agent-avatar";
import { SpaceSwitcher } from "@/workshop/components/canvas/space-switcher";
import type { Space } from "@/workshop/components/canvas/space-switcher";

/**
 * Workshop: App header — floating glass islands.
 *
 * Two floating pills (left + right) with transparent middle.
 * Theme toggle uses local state (no useTheme hook dependency).
 */

type Theme = "dark" | "light";

interface AppHeaderProps {
  onToggleSidebar?: () => void;
  onToggleRightPanel?: () => void;
  projectName?: string;
  spaceName?: string;
  spaces?: Space[];
  onSelectSpace?: (spaceId: string) => void;
  onAddSpace?: () => void;
  userLetter?: string;
  userColor?: string;
  /** Number of unread notifications (shown as badge on bell) */
  notificationCount?: number;
  onSearch?: () => void;
  onNotifications?: () => void;
  onUserMenu?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function AppHeader({
  onToggleSidebar,
  onToggleRightPanel,
  projectName,
  spaceName,
  spaces,
  onSelectSpace,
  onAddSpace,
  userLetter = "D",
  userColor = "#818CF8",
  notificationCount = 0,
  onSearch,
  onNotifications,
  onUserMenu,
  className,
  "data-testid": testId = "app-header",
}: AppHeaderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === "undefined") return "dark";
    const stored = localStorage.getItem("theme");
    return stored === "light" ? "light" : "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("dark", "light");
    root.classList.add(theme);
  }, [theme]);

  function toggleTheme() {
    const next: Theme = theme === "dark" ? "light" : "dark";
    localStorage.setItem("theme", next);
    setTheme(next);
  }

  return (
    <header
      className={cn(
        "pointer-events-none relative flex h-12 w-full shrink-0 items-center justify-between px-3 py-2",
        className,
      )}
      data-testid={testId}
    >
      {/* Left island: sidebar toggle + project/space breadcrumb — matches sidebar width */}
      <div
        className="pointer-events-auto flex h-9 w-60 items-center gap-0.5 rounded-full border border-glass-stroke bg-glass-fill pl-1 pr-2 shadow-glass-sm backdrop-blur-glass-sm"
        data-testid="header-left"
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={onToggleSidebar}
          data-testid="sidebar-toggle"
          aria-label="Toggle sidebar"
        >
          <Icons.PanelLeft className="h-4 w-4" />
        </Button>

        {(projectName || spaceName) && (
          <div
            className="flex min-w-0 flex-1 items-center gap-1"
            data-testid="header-project-space"
          >
            {projectName && (
              <span className="truncate text-[11px] font-semibold text-primary">
                {projectName}
              </span>
            )}
            {projectName && spaceName && (
              <Icons.ChevronRight
                className="h-3 w-3 shrink-0 text-muted"
                aria-hidden="true"
              />
            )}
            {spaceName && (
              <span className="shrink-0 text-[11px] font-medium text-secondary">
                {spaceName}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Center island: space switcher */}
      {spaces && spaces.length > 0 && (
        <div
          className="pointer-events-auto absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          data-testid="header-center"
        >
          <SpaceSwitcher
            spaces={spaces}
            onSelect={onSelectSpace ?? (() => {})}
            onAddSpace={onAddSpace}
          />
        </div>
      )}

      {/* Right island: search → right-panel toggle → theme → avatar — matches right panel width */}
      <div
        className="pointer-events-auto flex h-9 w-60 items-center gap-0.5 rounded-full border border-glass-stroke bg-glass-fill p-1 shadow-glass-sm backdrop-blur-glass-sm"
        data-testid="header-right"
      >
        {/* Search pill — flex to fill remaining width */}
        <button
          type="button"
          onClick={onSearch}
          className="flex h-7 flex-1 items-center gap-1.5 rounded-full px-2 text-muted transition-colors hover:text-secondary"
          data-testid="header-search"
          aria-label="Search"
        >
          <Icons.Search className="h-4 w-4 shrink-0" />
          <span className="text-[11px]">Search</span>
        </button>

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={onToggleRightPanel}
          data-testid="header-toggle-right-panel"
          aria-label="Toggle right panel"
        >
          <Icons.PanelRight className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={toggleTheme}
          data-testid="theme-toggle"
          aria-label={
            theme === "dark" ? "Switch to light mode" : "Switch to dark mode"
          }
        >
          {theme === "dark" ? (
            <Icons.Moon className="h-4 w-4" />
          ) : (
            <Icons.Sun className="h-4 w-4" />
          )}
        </Button>

        {/* Notification bell */}
        <Button
          variant="ghost"
          size="icon"
          className="relative h-7 w-7 shrink-0"
          onClick={onNotifications}
          data-testid="header-notifications"
          aria-label="Notifications"
        >
          <Icons.Bell className="h-4 w-4" />
          {notificationCount > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-500 px-0.5 text-[8px] font-bold leading-none text-white"
              data-testid="header-notification-badge"
            >
              {notificationCount > 9 ? "9+" : notificationCount}
            </span>
          )}
        </Button>

        {/* Avatar — concentric circle, ml-1 adds breathing room from bell */}
        <button
          type="button"
          className="ml-1 shrink-0"
          onClick={onUserMenu}
          data-testid="header-user-menu"
          aria-label="User menu"
        >
          <AgentAvatar
            letter={userLetter}
            color={userColor}
            size="default"
            hideStatus
          />
        </button>
      </div>
    </header>
  );
}

export type { AppHeaderProps };
